import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface UserRef {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
}

export interface PermissionConnect {
  chat: boolean;
  meeting: boolean;
}

export interface PermissionAttendance {
  admin: boolean;
  employee: boolean;
}

export interface PermissionEmployees {
  department: boolean;
  leaveEmployees: boolean;
  adminLeave: boolean;
  leaveConfiguration: boolean;
  allEmployees: boolean;
}

export interface PermissionReports {
  leaveReports: boolean;
  attendanceReports: boolean;
}

export interface Permissions {
  connect?: PermissionConnect;
  attendance?: PermissionAttendance;
  employees?: PermissionEmployees;
  reports?: PermissionReports;

  dashboard?: boolean;
  projects?: boolean;
  allProjects?: boolean;
  notifications?: boolean;
  ourClients?: boolean;
  userProfile?: boolean;
  holidays?: boolean;
  adminSettings?: boolean;
  superAdminPanel?: boolean;

  [key: string]: any;
}

export interface UserPermission {
  _id: string;
  userId: UserRef;
  role: string;
  organizationId?: string;
  permissions: Permissions;
  custom?: boolean;
  createdBy?: UserRef;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export interface UserPermissionsResponse {
  success: boolean;
  data: UserPermission;
  message?: string;
}

export const fetchUserPermissions =
  async (): Promise<UserPermissionsResponse> => {
    const response = await customAxios.get<UserPermissionsResponse>(
      `/permissions/users`
    );
    return response.data;
  };

export const useAllGetUsersAndPermissions = () =>
  useQuery<UserPermissionsResponse, AxiosError>(
    ["all-user-permissions"],
    () => fetchUserPermissions(),
    {
      // onError: (error) =>
      //   showNotification({
      //     title: "Error Loading Permissions",
      //     message:
      //       (error.response?.data as { message?: string })?.message ??
      //       "Failed to load user permissions",
      //     color: "red",
      //   }),
      retry: 1,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
// Define a proper type for the payload
interface UpdateUserPermissionPayload {
  id: string;
  data: Record<string, any>; // You can refine this type if you know the shape of permissions
}

// Define the response type
interface UpdateUserPermissionResponse {
  message: string;
  [key: string]: any; // If the API returns more data
}

export const UpdateUserPermission = async (
  payload: UpdateUserPermissionPayload
): Promise<UpdateUserPermissionResponse> => {
  const res = await customAxios.put<UpdateUserPermissionResponse>(
    `/permissions/users/${payload.id}`,
    payload.data
  );
  return res.data;
};

export const useUpdateUserPermission = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateUserPermissionResponse,
    unknown,
    UpdateUserPermissionPayload
  >({
    mutationFn: UpdateUserPermission,
    onSuccess: async (res) => {
      showNotification({
        title: "Permissions updated",
        message: res.message || "Permission updated successfully",
        color: "green",
      });
      console.log("Response of the updating permissions : ", res);
      await queryClient.invalidateQueries(["all-user-permissions"]);
      await queryClient.invalidateQueries(["userPermissions"]);
      await queryClient.refetchQueries(["all-user-permissions"], {
        exact: true,
      });
    },
    onError: () => {
      showNotification({
        title: "Update failed",
        message: "Something went wrong",
        color: "red",
      });
    },
    retry: 1,
  });
};
